/**
 * La tablet sin conexión (6.2; G417, G423, G424, G784): la sesión con PIN sigue valiendo sin red hasta 24 h después de la
 * **última respuesta del servidor** (`server_time`). La tablet lo calcula sola, sin pedirlo:
 *
 * - «Última conexión: hoy a las 22:22. Puedes seguir validando hasta mañana hacia las 22:20.»: el tope, redondeado a la
 *   baja (a los 10 minutos: no promete el minuto).
 * - Desde **2 h antes** del tope (G784), franja roja «Conéctate para no perder lo leído» con «Quedan menos de 2 h sin
 *   conexión. Última conexión: … Puedes seguir validando hasta …» (sin las llegadas por enviar, como en el dibujo).
 * - Pasado el tope, «Sesión caducada: vuelve a conectarte» y deja de validar (lo leído antes se envía al volver).
 */
export const TOPE_SIN_CONEXION_MS = 24 * 3_600_000;
export const AVISO_ANTES_MS = 2 * 3_600_000;
const REDONDEO_MS = 10 * 60_000;

export type EstadoSinConexion = 'normal' | 'conectate' | 'caducada';

export interface AvisoSinConexion {
    estado: EstadoSinConexion;
    /** El tope que se enseña, redondeado a la baja. */
    hasta: Date;
}

export function avisoSinConexion(ultimaRespuesta: Date, ahora: Date): AvisoSinConexion {
    const tope = ultimaRespuesta.getTime() + TOPE_SIN_CONEXION_MS;
    const hasta = new Date(Math.floor(tope / REDONDEO_MS) * REDONDEO_MS);
    const falta = tope - ahora.getTime();
    const estado: EstadoSinConexion = falta <= 0 ? 'caducada' : falta <= AVISO_ANTES_MS ? 'conectate' : 'normal';
    return { estado, hasta };
}

export const TEXTOS_SIN_CONEXION = {
    conectate: 'Conéctate para no perder lo leído',
    /** Delante de «Última conexión…» en la franja roja de las 2 h (6.2, G784). */
    quedanMenosDe2h: 'Quedan menos de 2 h sin conexión.',
    caducada: 'Sesión caducada: vuelve a conectarte',
} as const;

/**
 * El texto de la franja sin conexión con copia: con la franja roja de las 2 h, «Quedan menos de 2 h sin conexión.» delante
 * de «Última conexión…» y sin las llegadas por enviar (6.2, G784); si no, las llegadas por enviar (si hay) delante.
 */
export function textoFranjaSinConexion(estado: EstadoSinConexion, ultimaConexion: string, porEnviar: string | null): string {
    if (estado === 'conectate') {
        return `${TEXTOS_SIN_CONEXION.quedanMenosDe2h} ${ultimaConexion}`;
    }
    return porEnviar === null ? ultimaConexion : `${porEnviar} ${ultimaConexion}`;
}
